import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import Toolbar, { Item } from 'devextreme-react/toolbar'
import Button from 'devextreme-react/button'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'
import { createSelector } from '@reduxjs/toolkit'
import TextArea from 'devextreme-react/text-area'

import { RootState } from 'slices'
import { Notification, NotificationType, toggleTimeoutIdThunk, removeNotificationsByIdThunk } from 'slices/notifications'
import { IxConfirmPopup, Spinner } from 'components'
import { Popup } from 'devextreme-react/popup'

import { backgroundJobsColumnValue } from '../../pages/background-jobs'
import { BackgroundJobsFiles } from '../../pages/background-jobs/background-jobs-files'
import { ProtokollAufgabeType } from '../../pages/background-jobs/background-jobs-columns-enum'
import client from 'api'

import './notification-box.scss'

const toolbarHeight = 36

export interface INotificationMessage {
    type: 'info' | 'warning' | 'error' | 'job',
    text: string,
}

const notifyClasses = {
    info: 'ix-notification-info',
    warning: 'ix-notification-warning',
    error: 'ix-notification-error',
    job: 'ix-notification-job'
}

const getNotificationsByType = (notifications: Notification[], type: NotificationType) => {
    const res: Notification[] = []
        res.push(...notifications.filter(n => n.type === type))
    return res
}

const getBlockStyle = (nMessages: number) => {
    return nMessages ? {} : { display: 'none' }
}

const panelsStateSelector = createSelector(
    [
        (state: RootState) => state.ui.actionPanelState,
        (state: RootState) => state.ui.tabsPanelState,
    ],
    (actionPanelState, tabsPanelState) => ({
        actionPanelState,
        tabsPanelState,
    })
)

export const NotificationBox = () => {
    const contentRef = useRef<HTMLDivElement>(null)
    const { actionPanelState, tabsPanelState } = useSelector(panelsStateSelector)
    const { notifications, keys } = useSelector((state: RootState) => state.notifications)

    const [open, setOpen] = useState(false) // start in closed state and open right away in useEffect to make sure contentRef is already set
    const [hidden, setHidden] = useState(true)
    const [height, setHeight] = useState(toolbarHeight)

    const [jobItems, setJobItems] = useState<React.ReactNode[]>([])
    const [infoItems, setInfoItems] = useState<React.ReactNode[]>([])
    const [warningItems, setWarningItems] = useState<React.ReactNode[]>([])
    const [errorItems, setErrorItems] = useState<React.ReactNode[]>([])
    const [nItems, setNItems] = useState(0)
    const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)
    const [infoJobPopupVisible, setInfoJobPopupVisible] = useState(false)
    const [deleteNtf, setDeleteNtf] = useState(NaN)
    const [selectedJob, setSelectedJob] = useState<any>({})
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const columnValue = useMemo(() => (backgroundJobsColumnValue(t)),[t])
    const [files, setFiles] = useState<any>({})

    const [nBoxText, setNBoxText] = useState('')

    const marginRight = useMemo(() => {
        let margin = 40
        if (actionPanelState === 'right') { margin += 15 }
        else { margin -= 20}

        if (tabsPanelState === 'right') { margin += 30 }
        else { margin -= 20}

        return margin
    }, [actionPanelState, tabsPanelState])


    const onConfirmDeleteNtf = useCallback(() => {
        dispatch(removeNotificationsByIdThunk(deleteNtf))
        const filteredFiles: any = {}
        Object.keys(files).filter((id: any) => id !== deleteNtf).forEach((id: any) => {filteredFiles[id] = files[id]})
        selectedJob.HDR_ID === Number(deleteNtf) && setSelectedJob({})
        setFiles(filteredFiles)
        setConfirmPopupVisible(false)
    },[deleteNtf, dispatch, files, selectedJob.HDR_ID])

    const onDeleteNotification = useCallback((e: any) => {
        setDeleteNtf(e.currentTarget.id)
        setConfirmPopupVisible(true)
    },[])

    const toggleExpireTime = useCallback((e: any) => {
        dispatch(toggleTimeoutIdThunk(e.currentTarget.id))
    },[dispatch])

    const onFocusField = useCallback((e: any) => {
        // document.getElementById(e.currentTarget.id)?.focus()
        // document.getElementsByName(e.currentTarget.id)[1]?.focus();
        const key = e.currentTarget.dataset.key;
        const id = e.currentTarget.dataset.id;
        if (id === undefined) { return }
        (document.querySelector(`#${key} input[name=${id}]:not([type=hidden])`) as HTMLInputElement)?.focus()
    },[])

    const onShowJobInfo = useCallback((e: any) => {
        const job: any = notifications.find(n => n.id === e.currentTarget.id)
        setSelectedJob(job.data)
        setInfoJobPopupVisible(true)
    },[notifications])

    useEffect(() => {
        if (infoJobPopupVisible) {
            const updatedJob: any = notifications.find(n => Number(n.id) === selectedJob.HDR_ID)
            if (updatedJob && updatedJob?.data.CURRENTSTATE !== selectedJob.CURRENTSTATE) { setSelectedJob(updatedJob.data) }
        }
    },[notifications, selectedJob, infoJobPopupVisible])

    useEffect(() => {
        if (infoJobPopupVisible && files[selectedJob.HDR_ID]?.length === 1) {
            setTimeout(() => {
                const openButton: any = document.querySelector('.dx-popup-content [title="open"]')
                openButton?.click()
            }, 500)
        }
    },[infoJobPopupVisible, files, selectedJob])

    useEffect(() => {
        if (!files[selectedJob.HDR_ID] && selectedJob.CURRENTSTATE === 2) {
            client.get(`api/ixweb/list_folder/?foldername=ixjob${`${selectedJob.HDR_ID}`.padStart(9, '0')}`).then((res: any) => {
                setFiles((files: any) => ({...files, [selectedJob.HDR_ID]: res.data}))
            })
        }
    },[selectedJob, files])

    useEffect(() => {
        if (open && contentRef.current) {
            const nBoxContent: any = contentRef.current
            if (nBoxText !== nBoxContent.outerText) {
                setHeight(contentRef.current.scrollHeight)
                setNBoxText(nBoxContent.outerText)
            }
        }
    },[open, nBoxText, jobItems])

    const itemConstructor = useCallback((m: Notification, i: number) => {
        const protocolText: any = (m.type === 'job' &&  m.data.PROTOKOLLTEXT) ? m.data.PROTOKOLLTEXT.split(/\r?\n\n?/) : []
        return (
            <li className={notifyClasses[m.type]} key={i} {...(m.type === 'error' ? {'data-id': m.name, 'data-key': m.key} : {})} onClick={onFocusField}>
                <span {...m.type === 'job' ? {id: m.id, onClick: onShowJobInfo} : {}} >
                    {m.type === 'job' ? `${m.msg} ${columnValue.currentState[m.data.CURRENTSTATE]}` : m.msg}
                    {
                        m.type === 'job' && protocolText.length
                            ? <div>
                                {protocolText[0] || ''}<br/>
                                {protocolText[1] || ''}

                            </div>
                            : ''
                    }
                    </span>
                {
                    m.expireTime &&
                    (
                        <div>
                            <div className={`btn ${notifyClasses[m.type]}`}>
                                <i className='dx-icon-trash' title='delete' id={m.id} onClick={onDeleteNotification}/>
                            </div>
                            <div className={`btn ${notifyClasses[m.type]} btn-pin`}>
                                <i className={`dx-icon-pin ${m.pinned && 'btn-pin--selected' }`} title='pin' id={m.id} onClick={toggleExpireTime} />
                            </div>
                        </div>
                    )
                }
            </li>
        )

    },[toggleExpireTime, onShowJobInfo, columnValue, onFocusField, onDeleteNotification])

    useEffect(() => {
        const lastFormKey = keys.find(k => k.endsWith('-form'))
        const _notifications = notifications.filter(n => !n.key.endsWith('-form') || n.key === lastFormKey)
        const tmpJob = getNotificationsByType(_notifications, 'job').map(itemConstructor)
        const tmpInfo = getNotificationsByType(_notifications, 'info').map(itemConstructor)
        const tmpWarning = getNotificationsByType(_notifications, 'warning').map(itemConstructor)
        const tmpError = getNotificationsByType(_notifications, 'error').map(itemConstructor)

        setJobItems(tmpJob)
        setInfoItems(tmpInfo)
        setWarningItems(tmpWarning)
        setErrorItems(tmpError)
        setNItems(tmpInfo.length + tmpWarning.length + tmpError.length + tmpJob.length)
        notifications.length === 0 && setOpen(false)
    }, [notifications, itemConstructor, keys])

    const getHeight = useCallback(() => {
        if (open && nItems && contentRef.current) {
            const nBoxContent: any = contentRef.current
            setNBoxText(nBoxContent.outerText)
            setHeight(contentRef.current.scrollHeight)
        } else {
            setHeight(0)
        }
    }, [open, nItems])

    const onTransitionEnd = useCallback((e: any) => {
        if (e.propertyName === 'width') {
            getHeight();
        }
    }, [getHeight])

    const onClick = useCallback(() => {
        setOpen(!open)
    }, [open])

    useEffect(() => {
        let timer1: any
        if (nItems) {
            setHidden(false)
        } else {
            timer1 = setTimeout(() => {
                setHidden(true)
            }, 1000)
        }
        return () => clearTimeout(timer1)
    }, [nItems])

    useEffect(() => {
        getHeight()
    }, [getHeight])

    const getBadgeClass = useCallback(() => {
        const className = 'ix-notification-bg-'
        if (errorItems.length > 0) {
            return `${className}error`
        }
        else if (warningItems.length > 0) {
            return `${className}warning`
        }
        else if (jobItems.length > 0) {
            return `${className}job`
        }
        else {
            return `${className}info`
        }


    }, [errorItems, warningItems, jobItems])

    const BtnRender = useCallback(() => (
        <div style={ { display: 'flex', alignItems: 'center' } }>
            <Button onClick={onClick} stylingMode='text' icon='chevronup' className={`${getBadgeClass()} ${!open && 'notification-arrow-btn-closed '}`} >
                <i style={{ display: 'initial'}} className={`dx-icon dx-icon-${open ? 'chevronup' : 'chevrondown'}`}/>
            </Button>
        </div>

    ), [getBadgeClass, open, onClick])

    const Badge = useCallback(({ type, text }: { type: string, text: number }) => {
        return (
            <div
                className={`ix-notification-bg-${type}`}
                style={{...getBlockStyle(text),
                    minWidth: !open ? 15 : 20,
                    height: !open ? 15 : 20,
                    fontSize: !open ? '0.8em' : '1em',
                 }}
                title={`${text} ${type}${text > 1 ? 's' : ''}`}
            >
                <span style={{lineHeight: 1}} >{text}</span>
            </div>
        )
    }, [open])

    const closedBubbleWidth = () => {
        const bubble = [errorItems.length, warningItems.length, infoItems.length, jobItems.length]
        let width = 205;
        bubble.forEach((i) => {
            if (i > 99) {
                width = 220
            }
        })
        return width
    }

    const mostSevereType = useMemo(() => {
        if (errorItems.length) { return 'error' }
        else if (warningItems.length) { return 'warning' }
        else { return 'info' }
    }, [errorItems.length, warningItems.length])

    const titleTranslationCode  = useMemo(() => {
        let translationCode = ''
        if (errorItems.length) { translationCode += 'errors' }
        if (warningItems.length) { translationCode += 'warnings' }
        if (translationCode.length === 0) { translationCode = 'notifications' }

        return 'global:label.' + translationCode
    }, [errorItems.length, warningItems.length])

    return createPortal(
        <div
            data-testid='dx-overlay-content'
            className='ix-notification-box-overlay'
        >
            <div
                data-testid='ix-notification-box'
                onTransitionEnd={onTransitionEnd}
                className={`ix-notification-box ix-notification-box-${mostSevereType} ${hidden ? 'display-none' : ''}`}
                style={ {
                    height: height + toolbarHeight - 5,
                    width: !open ? closedBubbleWidth() : '',
                    marginRight: marginRight
                } }
            >
                <Toolbar elementAttr={{ style: `height: ${open ? toolbarHeight : toolbarHeight - 5}px`}} >
                    <Item
                        location='before'
                        text={t(titleTranslationCode)}
                        cssClass={ !open ? 'ix-notification-box--title' : ''}
                    />

                    <Item location='before' cssClass={`ix-notifications-badge ${errorItems.length === 0 && 'display-none'}`} >
                        <Badge type='error' text={errorItems.length}/>
                    </Item>

                    <Item location='before' cssClass={`ix-notifications-badge ${warningItems.length === 0 && 'display-none'}`} >
                        <Badge type='warning' text={warningItems.length}/>
                    </Item>

                    <Item location='before' cssClass={`ix-notifications-badge ${infoItems.length === 0 && 'display-none'}`}>
                        <Badge type='info' text={infoItems.length}/>
                    </Item>

                    <Item location='before' cssClass={`ix-notifications-badge ${jobItems.length === 0 && 'display-none'}`}>
                        <Badge type='job' text={jobItems.length}/>
                    </Item>

                    <Item location='after'>
                        <BtnRender />
                    </Item>
                </Toolbar>

                <div className='ix-notification-box--wrapper'>
                    <div ref={contentRef} className='ix-notification-box--content'>

                        <div style={getBlockStyle(errorItems.length)}>
                            <h5 className='ix-notification-error'><i className='dx-icon-warning' /> {t('global:label.errors')}:</h5>
                            <ul className='ix-notifications-list'>{ errorItems }</ul>
                        </div>

                        <div style={getBlockStyle(warningItems.length)}>
                            <h5 className='ix-notification-warning'><i className='dx-icon-warning' /> {t('global:label.warnings')}:</h5>
                            <ul className='ix-notifications-list'>{ warningItems }</ul>
                        </div>

                        <div style={getBlockStyle(infoItems.length)}>
                            <h5 className='ix-notification-info'><i className='dx-icon-warning' /> {t('global:label.info')}:</h5>
                            <ul className='ix-notifications-list'>{ infoItems }</ul>
                        </div>

                        <div style={getBlockStyle(jobItems.length)}>
                            <h5 className='ix-notification-job'><i className='dx-icon-warning' /> {t('global:label.jobs')}:</h5>
                            <ul className='ix-notifications-list'>{ jobItems }</ul>
                        </div>

                    </div>
                </div>

                <IxConfirmPopup
                    popupVisible={confirmPopupVisible}
                    title={t('global:label.warning')}
                    description={t('global:question.deleteNotification')}
                    onConfirm={onConfirmDeleteNtf}
                    onHiding={() => setConfirmPopupVisible(false)}
                />

                <Popup
                    visible={infoJobPopupVisible}
                    onHiding={() => setInfoJobPopupVisible(false)}
                    showTitle={true}
                    title={t(`bg-jobs:enum.ProtokollAufgabeType.${ProtokollAufgabeType[selectedJob.AUFGABETYPE]}`)}
                    shading={false}
                    showCloseButton={true}
                    width={1000}
                    height='auto'
                    className='confirm-popup'
                    hideOnOutsideClick= {true}
                >
                    <div className='form'>
                        {
                            selectedJob.CURRENTSTATE === 2
                                ? <BackgroundJobsFiles selectedRowFiles={files[selectedJob.HDR_ID]}/>
                                : <div style={{textAlign: 'center'}}>
                                    <p>In Progress...</p>
                                    <Spinner />
                                </div>
                        }
                        <div className='rb-item mb-1'>
                            <label htmlFor='' className='dx-field-label'>{t('bg-jobs:field.PROTOKOLLTEXT')}</label>
                            <TextArea
                                autoResizeEnabled={true}
                                minHeight={26}
                                readOnly={true}
                                value={selectedJob.PROTOKOLLTEXT}
                            />
                        </div>
                    </div>
                </Popup>
            </div>
        </div>,
        document.getElementById('ix-notification-box-portal')!
    )
}
